import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Menu,
  MenuItem,
  Button,
  Chip,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Home,
  Event,
  Public,
  Store,
  Person,
  Park,
  TheaterComedy,
  IceSkating,
  Restaurant,
  ShoppingBag,
  Celebration,
  Favorite,
} from '@mui/icons-material';

const categoryIcons = {
  house: Home,
  event: Event,
  community: Public,
  market: Store,
  santa: Person,
  tree_farm: Park,
  show: TheaterComedy,
  skating: IceSkating,
  restaurant: Restaurant,
  shopping: ShoppingBag,
  parade: Celebration,
  charity: Favorite,
};

const CategoryDropdownWeb = ({ selectedCategories = [], onCategoryChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const categories = [
    { id: 'all', label: 'All Locations', icon: null },
    { id: 'house', label: 'Houses', icon: categoryIcons.house },
    { id: 'event', label: 'Events', icon: categoryIcons.event },
    { id: 'community', label: 'Community Lights', icon: categoryIcons.community },
    { id: 'market', label: 'Markets', icon: categoryIcons.market },
    { id: 'santa', label: 'Santa', icon: categoryIcons.santa },
    { id: 'tree_farm', label: 'Tree Farms', icon: categoryIcons.tree_farm },
    { id: 'show', label: 'Shows', icon: categoryIcons.show },
    { id: 'skating', label: 'Ice Skating', icon: categoryIcons.skating },
    { id: 'restaurant', label: 'Dining', icon: categoryIcons.restaurant },
    { id: 'shopping', label: 'Shopping', icon: categoryIcons.shopping },
    { id: 'parade', label: 'Parades', icon: categoryIcons.parade },
    { id: 'charity', label: 'Charity', icon: categoryIcons.charity },
  ];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (categoryId) => {
    if (categoryId === 'all') {
      onCategoryChange(['all']);
    } else {
      const newSelection = selectedCategories.includes('all')
        ? [categoryId]
        : selectedCategories.includes(categoryId)
        ? selectedCategories.filter((id) => id !== categoryId)
        : [...selectedCategories.filter((id) => id !== 'all'), categoryId];
      
      if (newSelection.length === 0) {
        onCategoryChange(['all']);
      } else {
        onCategoryChange(newSelection);
      }
    }
    handleClose();
  };

  const getButtonText = () => {
    if (selectedCategories.length === 0 || (selectedCategories.length === 1 && selectedCategories[0] === 'all')) {
      return 'All Categories';
    }
    if (selectedCategories.length === 1) {
      const category = categories.find((c) => c.id === selectedCategories[0]);
      return category ? category.label : 'Categories';
    }
    return `${selectedCategories.length} Categories Selected`;
  };

  const getButtonIcon = () => {
    if (selectedCategories.length === 1 && selectedCategories[0] !== 'all') {
      const category = categories.find((c) => c.id === selectedCategories[0]);
      return category?.icon;
    }
    return null;
  };

  const IconComponent = getButtonIcon();

  return (
    <Box>
      <Button
        variant="outlined"
        onClick={handleClick}
        startIcon={IconComponent ? <IconComponent /> : null}
        endIcon={open ? '▲' : '▼'}
        sx={{
          textTransform: 'none',
          borderColor: '#e0e0e0',
          color: '#333',
          backgroundColor: '#fff',
          borderRadius: '10px',
          padding: '8px 16px',
          fontSize: '14px',
          fontWeight: '600',
          minWidth: '160px',
          justifyContent: 'space-between',
          '&:hover': {
            borderColor: '#1B5E20',
            backgroundColor: '#f5f5f5',
          },
        }}
      >
        {getButtonText()}
        {selectedCategories.length > 1 && (
          <Chip
            label={selectedCategories.length}
            size="small"
            sx={{
              marginLeft: 1,
              height: '20px',
              fontSize: '11px',
              backgroundColor: '#8C1515',
              color: '#fff',
            }}
          />
        )}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: '250px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        {categories.map((category) => {
          const Icon = category.icon;
          const isSelected = selectedCategories.includes(category.id);
          
          return (
            <MenuItem
              key={category.id}
              onClick={() => handleSelect(category.id)}
              selected={isSelected}
              sx={{
                backgroundColor: isSelected ? 'rgba(27, 94, 32, 0.1)' : 'transparent',
                '&:hover': {
                  backgroundColor: isSelected ? 'rgba(27, 94, 32, 0.15)' : 'rgba(0, 0, 0, 0.05)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(27, 94, 32, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(27, 94, 32, 0.15)',
                  },
                },
              }}
            >
              {Icon && (
                <ListItemIcon>
                  <Icon sx={{ color: isSelected ? '#1B5E20' : '#666', fontSize: 20 }} />
                </ListItemIcon>
              )}
              <ListItemText
                primary={category.label}
                primaryTypographyProps={{
                  fontSize: '14px',
                  fontWeight: isSelected ? '600' : '400',
                  color: isSelected ? '#1B5E20' : '#333',
                }}
              />
              {isSelected && (
                <Box
                  sx={{
                    color: '#1B5E20',
                    fontWeight: 'bold',
                    marginLeft: 1,
                  }}
                >
                  ✓
                </Box>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default CategoryDropdownWeb;

